<script lang="ts">
  import Dialog from '@leaflink/stash/Dialog.vue';
  import { StepperRef } from '@leaflink/stash/Stepper.vue';
  import { computed, inject, onMounted, onUnmounted, provide, ref, useSlots, watch } from 'vue';

  import {
    FeatureEnrollmentCustomEvents,
    FeatureEnrollmentEmitterArgs,
    FeatureEnrollmentGoToStepArgs,
    FeatureEnrollmentLayoutProps,
    FeatureEnrollmentProviderProps,
  } from '@/models/featureEnrollment';
  import i18n from '@/plugins/i18n';

  import FeatureEnrollmentBody from './FeatureEnrollmentBody.vue';
  import FeatureEnrollmentSidenav from './FeatureEnrollmentSidenav.vue';

  export const FEATURE_ENROLLMENT_INJECTION_KEY = 'feature-enrollment';
  export const DESKTOP_SCREEN_MIN_WIDTH = 961;
  export const HEADER_MAX_HEIGHT = 168;
  export const HEADER_MIN_HEIGHT = 80;
  export const SIDENAV_WIDTH = 348;

  const customEventsInit: FeatureEnrollmentCustomEvents = {
    beforeNext() {},
    beforeBack() {},
    beforeGoToStep() {},

    onEnroll() {},
  };

  // Exporting this for unit testing purposes
  export const customEvents: FeatureEnrollmentCustomEvents = { ...customEventsInit };

  export const useFeatureEnrollmentProvider = (options?: { events?: Partial<FeatureEnrollmentCustomEvents> }) => {
    Object.assign(customEvents, options?.events);

    const featureEnrollmentInjection = inject<FeatureEnrollmentProviderProps>(FEATURE_ENROLLMENT_INJECTION_KEY);

    if (!featureEnrollmentInjection) {
      throw new Error(
        `Could not resolve ${FEATURE_ENROLLMENT_INJECTION_KEY}. This composable must be used within <FeatureEnrollmentLayout>.`,
      );
    }

    return featureEnrollmentInjection;
  };
</script>

<script setup lang="ts">
  const slots = useSlots();

  const layoutProps = defineProps<FeatureEnrollmentLayoutProps>();

  const emits = defineEmits<{
    (e: 'onNext', props: FeatureEnrollmentEmitterArgs): void;
    (e: 'onBack', props: FeatureEnrollmentEmitterArgs): void;
    (e: 'onStepClick', props: FeatureEnrollmentEmitterArgs): void;
    (e: 'onStepChange', props: FeatureEnrollmentEmitterArgs): void;
    (e: 'onOpenExitDialog', props: FeatureEnrollmentEmitterArgs): void;
    (e: 'onDismissExitDialog', props: FeatureEnrollmentEmitterArgs): void;
    (e: 'onExit', props: FeatureEnrollmentEmitterArgs): void;
  }>();

  const alertRef = ref<HTMLElement | undefined>();
  const bodyRef = ref<HTMLElement | undefined>();
  const contentRef = ref<HTMLElement | undefined>();
  const footerRef = ref<HTMLElement | undefined>();
  const headerRef = ref<HTMLElement | undefined>();
  const layoutRef = ref<HTMLElement | undefined>();
  const sidenavRef = ref<HTMLElement | undefined>();
  const stepperRef: StepperRef = ref(null);

  const activeStepIndexInit = layoutProps.stepperOptions?.step || 0;
  const activeSubstepIndexInit = layoutProps.stepperOptions?.substep || 0;

  const activeStepIndex = ref(activeStepIndexInit);
  const activeSubstepIndex = ref(activeSubstepIndexInit);

  const exitButtonText = ref(i18n.t('common.exit'));
  const nextButtonText = ref(i18n.t('common.next'));
  const backButtonText = ref(i18n.t('common.back'));
  const enrollButtonText = ref(i18n.t('common.enroll'));

  const isProcessing = ref(false);
  const isEnrollmentComplete = ref(false);
  const isNavForwardDisabled = ref(false);
  const isExitDialogOpen = ref(false);
  const isSidenavActive = ref(false);

  const toPixels = (number: number) => number + 'px'; // utilized in styles section down below ↓↓↓

  const getEmitterArgs = (event?: Event): FeatureEnrollmentEmitterArgs => {
    return {
      event,
      activeStepIndex: activeStepIndex.value,
      activeSubstepIndex: activeSubstepIndex.value,
      currentStep: currentStep.value,
      nextStep: nextStep.value,
      previousStep: previousStep.value,
    };
  };

  const getStep = (position: 'previous' | 'current' | 'next') => {
    let index = activeStepIndex.value;
    let subIndex = activeSubstepIndex.value;

    if (position === 'previous') {
      index--;
      subIndex--;
    }

    if (position === 'next') {
      index++;
      subIndex++;
    }

    return layoutProps.steps[index]?.substeps?.[subIndex] || layoutProps.steps[index];
  };

  const previousStep = computed(() => getStep('previous'));
  const currentStep = computed(() => getStep('current'));
  const nextStep = computed(() => getStep('next'));

  const next = async (event: Event) => {
    if (isProcessing.value || isNavForwardDisabled.value) return;
    try {
      await customEvents.beforeNext();
      activeSubstepIndex.value = 0; // Clear substep flow history to keep it true linear
      stepperRef.value?.next();
      emits('onNext', getEmitterArgs(event));
    } catch (error) {
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } else {
        throw Error;
      }
    }
  };

  const back = async (event: Event) => {
    if (isProcessing.value) return;
    await customEvents.beforeBack();
    stepperRef.value?.back();
    emits('onBack', getEmitterArgs(event));
  };

  const goToStep = async ({ index, subIndex, event }: FeatureEnrollmentGoToStepArgs) => {
    if (isProcessing.value) return;
    if (index > activeStepIndex.value && isNavForwardDisabled.value) return;
    if (subIndex && subIndex > activeSubstepIndex.value && isNavForwardDisabled.value) return;
    await customEvents.beforeGoToStep();
    stepperRef.value?.goTo(index, subIndex);
  };

  const onStepClick = (args: FeatureEnrollmentGoToStepArgs) => {
    emits('onStepClick', getEmitterArgs(args.event));
    goToStep(args);
  };

  const openExitDialog = (event: Event) => {
    isExitDialogOpen.value = true;
    emits('onOpenExitDialog', getEmitterArgs(event));
  };

  const dismissExitDialog = () => {
    isExitDialogOpen.value = false;
    emits('onDismissExitDialog', getEmitterArgs());
  };

  const enroll = () => {
    if (isProcessing.value || isNavForwardDisabled.value) return;
    customEvents.onEnroll();
  };

  const exit = () => {
    if (isProcessing.value) return;
    emits('onExit', getEmitterArgs());
  };

  const onWindowResize = () => {
    isSidenavActive.value = window.innerWidth >= DESKTOP_SCREEN_MIN_WIDTH;
  };

  const resetCallbackEvents = () => {
    Object.assign(customEvents, customEventsInit);
  };

  const resetStatuses = () => {
    isProcessing.value = false;
    isEnrollmentComplete.value = false;
    isNavForwardDisabled.value = false;
    isSidenavActive.value = window.innerWidth >= DESKTOP_SCREEN_MIN_WIDTH;
  };

  const resetNavButtonsText = () => {
    exitButtonText.value = i18n.t('common.exit');
    nextButtonText.value = i18n.t('common.next');
    backButtonText.value = i18n.t('common.back');
    enrollButtonText.value = i18n.t('common.enroll');
  };

  // Resets the state on step/substep change
  watch([activeStepIndex, activeSubstepIndex], () => {
    resetCallbackEvents();
    resetStatuses();
    resetNavButtonsText();
    bodyRef.value?.scrollTo?.({ top: 0 });
    window.dispatchEvent(new Event('resize'));
    emits('onStepChange', getEmitterArgs());
  });

  provide<FeatureEnrollmentProviderProps>(FEATURE_ENROLLMENT_INJECTION_KEY, {
    ...layoutProps,

    alertRef,
    bodyRef,
    contentRef,
    footerRef,
    headerRef,
    layoutRef,
    sidenavRef,
    stepperRef,

    activeStepIndex,
    activeSubstepIndex,

    previousStep,
    currentStep,
    nextStep,

    exitButtonText,
    nextButtonText,
    backButtonText,
    enrollButtonText,

    isProcessing,
    isEnrollmentComplete,
    isNavForwardDisabled,
    isExitDialogOpen,
    isSidenavActive,

    next,
    back,
    goToStep,
    enroll,
    openExitDialog,
    exit,

    resetCallbackEvents,
    resetStatuses,
    resetNavButtonsText,

    slots,
  });

  onMounted(() => {
    window.addEventListener('resize', onWindowResize);
    onWindowResize();

    // Emits onStepChange to expose the currentStep information on mounted
    emits('onStepChange', getEmitterArgs());
  });

  onUnmounted(() => {
    resetCallbackEvents();
    window.removeEventListener('resize', onWindowResize);
  });
</script>

<template>
  <div
    id="feature-enrollment-layout"
    class="root tw-bg-white tw-grid tw-grid-cols-1 tw-min-height-screen lg:tw-grid-cols-[auto_1fr]"
    ref="layoutRef"
  >
    <FeatureEnrollmentSidenav @on-step-click="onStepClick" />
    <FeatureEnrollmentBody />

    <Dialog
      :description="i18n.t('featureEnrollment.onExitingDialog.description')"
      :header="i18n.t('featureEnrollment.onExitingDialog.header')"
      status="warning"
      :cancel-text="i18n.t('featureEnrollment.onExitingDialog.cancelButtonText')"
      :confirm-text="i18n.t('featureEnrollment.onExitingDialog.confirmButtonText')"
      :open="isExitDialogOpen"
      @cancel="dismissExitDialog"
      @confirm="exit"
    ></Dialog>
  </div>
</template>

<style scoped>
  /* stylelint-disable value-keyword-case -- v-bind exception */
  /* stylelint-disable function-name-case -- v-bind exception */
  .root {
    --header-max-height: v-bind(toPixels(HEADER_MAX_HEIGHT));
    --header-min-height: v-bind(toPixels(HEADER_MIN_HEIGHT));
    --sidenav-width: v-bind(toPixels(SIDENAV_WIDTH));
  }
</style>
